import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { TabPanel, TabularMenu, TabularMenuProvider } from '@joor/design-system'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import styled from 'styled-components'

import { LGConnectionStatus } from '__generated__/atlas-types'

import { fromGlobalId } from 'utils/transformations/graphql'

import { isAccountTypeRetailer } from 'store/currentUser/selectors'

import { RETAILER_VIEW_OWN_PROFILE } from 'routes/paths'

import { RetailerProfile } from '../retailerProfilePage.types'
import { useVisitToRetailerProfile } from './RetailerProfileViewMode.hooks'
import Header from './components/Header/Header'
import JoorActivityInsightsWidget from './components/JoorActivityInsightsWidget/JoorActivityInsightsWidget'
import NewRetailerProfileContent from './components/RetailerProfileContent/RetailerProfileContent'
import RetailerProfileContent from './components/RetailerProfileContent/RetailerProfileContent'

const PAGE_TOP_OFFSET = '53px'
const CUSTOMER_DATA_TAB_CLASS = 'customer-data-tab'

const StyledContainer = styled.div`
  position: absolute;
  top: -25px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  background-color: #f6f6f6;
`
const StyledConnectedLayout = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &.${CUSTOMER_DATA_TAB_CLASS} {
    display: flex;
    flex-direction: column;
    height: calc(100vh - ${PAGE_TOP_OFFSET});
    overflow: hidden;
  }
`
const StyledTabsWrapper = styled.div`
  width: 100%;

  .${CUSTOMER_DATA_TAB_CLASS} & {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
`
const StyledTabPanelArea = styled.div`
  width: 100%;

  .${CUSTOMER_DATA_TAB_CLASS} & {
    flex: 1;
    min-height: 0;
  }
`
const StyledTabularMenu = styled(TabularMenu)`
  padding-left: 32px;
  flex-shrink: 0;
`
const StyledProfileTabContainer = styled.div`
  padding-top: 16px;
`
const CustomerDataTabContainer = styled.div`
  height: 100%;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`
const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`

const InsightsWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0 32px 24px;
`

export const CUSTOMER_DATA_PARAM = 'customer-data'

type NewRetailerProfileViewModeProps = {
  setEditMode: Dispatch<SetStateAction<boolean>>
  retailerData: RetailerProfile
  onHeaderActionSuccess: () => void
}

const RetailerProfileViewMode = ({
  setEditMode,
  retailerData: retailer,
  onHeaderActionSuccess,
}: NewRetailerProfileViewModeProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const isRetailerAccount = useSelector(isAccountTypeRetailer)
  const { recordVisit } = useVisitToRetailerProfile(retailer.id)
  const isAccountConnected =
    retailer?.connectionStatus === LGConnectionStatus.CONNECTED

  const retailerName = retailer?.displayName
    ? retailer?.displayName
    : retailer?.name
  const retailerId = fromGlobalId(retailer.id)?.id
  const path = retailerId
    ? generatePath(RETAILER_VIEW_OWN_PROFILE, {
        retailerId,
        matchType: '',
      })
    : ''
  const legacyUrl = `${window.location.origin}${path}?${CUSTOMER_DATA_PARAM}`

  const yearsOrderedOnJoor = retailer.yearsOrdered ?? []
  const brandsOrderedOnJoor = retailer.brandsOrdered ?? []
  const categoriesOrderedOnJoor = retailer.categoriesOrdered ?? []
  const showJoorActivityInsights =
    yearsOrderedOnJoor.length > 0 ||
    brandsOrderedOnJoor.length > 0 ||
    categoriesOrderedOnJoor.length > 0

  const joorActivityInsights = showJoorActivityInsights ? (
    <InsightsWrapper>
      <JoorActivityInsightsWidget
        yearsOrderedOnJoor={yearsOrderedOnJoor.map(String)}
        brandsPurchasedOnJoor={brandsOrderedOnJoor}
        categoriesPurchasedOnJoor={categoriesOrderedOnJoor}
      />
    </InsightsWrapper>
  ) : null

  useEffect(() => {
    if (!isRetailerAccount && !isAccountConnected && retailer.id) {
      recordVisit()
    }
  }, [retailer.id, isRetailerAccount, isAccountConnected])

  useEffect(() => {
    if (isAccountConnected) {
      setSelectedTabIndex(1)
    }
  }, [isAccountConnected])

  const customerDataTabClassName =
    selectedTabIndex === 1 ? CUSTOMER_DATA_TAB_CLASS : undefined

  const header = (
    <Header
      retailerId={retailer?.id}
      retailerName={retailerName ?? ''}
      logoUrl={retailer?.logo?.url ?? ''}
      retailerProfile={retailer} /* TODO: refactor this so we don't 
          need to pass the entire retailer profile */
      taxId={retailer?.taxId}
      handleClick={() => {
        setEditMode(true)
      }}
      connectionStatus={retailer?.connectionStatus}
      onMutationSuccess={onHeaderActionSuccess}
      autoConnect={retailer?.autoConnect}
    />
  )

  return (
    <StyledContainer data-testid="retailer-profile__view-mode">
      {isAccountConnected ? (
        <StyledConnectedLayout className={customerDataTabClassName}>
          {header}
          <StyledTabsWrapper>
            <TabularMenuProvider
              tabs={[
                {
                  component: (
                    <StyledProfileTabContainer>
                      {joorActivityInsights}
                      <NewRetailerProfileContent
                        retailer={retailer}
                        isRetailerAccount={isRetailerAccount}
                        description={retailer?.description ?? ''}
                      />
                    </StyledProfileTabContainer>
                  ),
                  label: 'Profile',
                  onClick: () => setSelectedTabIndex(0),
                },
                {
                  component: (
                    <CustomerDataTabContainer>
                      <StyledIframe
                        title="Customer Specific Data"
                        src={legacyUrl}
                      />
                    </CustomerDataTabContainer>
                  ),
                  label: 'Customer Specific Data',
                  onClick: () => setSelectedTabIndex(1),
                },
              ]}
              index={selectedTabIndex}
            >
              <StyledTabularMenu transparentTabs={true} />
              <StyledTabPanelArea>
                <TabPanel />
              </StyledTabPanelArea>
            </TabularMenuProvider>
          </StyledTabsWrapper>
        </StyledConnectedLayout>
      ) : (
        <>
          {header}
          {joorActivityInsights}
          <RetailerProfileContent
            retailer={retailer}
            isRetailerAccount={isRetailerAccount}
            description={retailer?.description ?? ''}
          />
        </>
      )}
    </StyledContainer>
  )
}

export default RetailerProfileViewMode
